import { useEffect, useState } from "react";
/**
 * @export
 *
 * Hook to check if Phosphor Icons web components are loaded and ready
 *
 * This checks if the @phosphor-icons/webcomponents library has been loaded
 * by verifying if custom elements are defined.
 *
 * @example
 * ```tsx
 * const ready = useIsPHIconReady('house');
 * if (!ready) return <SkeletonLoader />;
 * return <ph-house weight="bold" />;
 * ```
 */
export const useIsPHIconReady = (iconName) => {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        // Check if custom elements API is available
        if (typeof window === 'undefined' || !window.customElements) {
            // Fallback: show icon anyway if API not available
            setReady(true);
            return;
        }
        // Use a probe icon to check if library is loaded
        // We'll check for 'ph-house' as a common icon that should always exist
        const probeIconName = iconName || 'house';
        const customElementName = `ph-${probeIconName}`;
        // Check if already defined
        if (customElements.get(customElementName)) {
            setReady(true);
            return;
        }
        // Wait for the custom element to be defined
        customElements.whenDefined(customElementName)
            .then(() => {
            setReady(true);
        })
            .catch(() => {
            // If it fails, show the icon anyway (graceful fallback)
            setReady(true);
        });
        // Fallback timeout - show icon after 5 seconds even if not loaded
        const timeout = setTimeout(() => {
            setReady(true);
        }, 5000);
        return () => {
            clearTimeout(timeout);
        };
    }, [iconName]);
    return ready;
};
